import type { PublicAccount } from '@postpilot/core/domain';
import { motion } from 'framer-motion';
import { AlertCircle, Plus, RefreshCw, Trash2, Users } from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Avatar, AvatarFallback, AvatarImage, Card, CardContent } from '@/components/ui/primitives';
import { AccountRowSkeleton, EmptyState, ErrorState } from '@/components/ui/states';
import { dayjs } from '@/lib/dayjs';
import { cn } from '@/lib/utils';
import { useAccountsStore } from '@/stores/accounts-store';

export function AccountsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const accounts = useAccountsStore((s) => s.accounts);
  const loading = useAccountsStore((s) => s.loading);
  const error = useAccountsStore((s) => s.error);
  const fetch = useAccountsStore((s) => s.fetch);
  const connect = useAccountsStore((s) => s.connect);
  const disconnect = useAccountsStore((s) => s.disconnect);

  const [confirmRemove, setConfirmRemove] = React.useState<PublicAccount | null>(null);

  /**
   * The OAuth callback redirects back here with `?connected=` or
   * `?connect_error=`. Surface it as a toast, then strip the param — otherwise
   * a refresh re-fires the toast, and the URL stays ugly.
   */
  React.useEffect(() => {
    const connected = searchParams.get('connected');
    const connectError = searchParams.get('connect_error');

    if (connected) {
      toast.success(`Connected @${connected}`, {
        description: 'You can now post to this account.',
      });
      void fetch();
    }

    if (connectError) {
      toast.error('Could not connect the account', { description: connectError });
    }

    if (connected || connectError) {
      searchParams.delete('connected');
      searchParams.delete('connect_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, fetch]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Accounts</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            The X accounts PostPilot can publish to on your behalf.
          </p>
        </div>

        <Button onClick={() => void connect('/accounts')}>
          <Plus />
          Connect account
        </Button>
      </div>

      <Card>
        {loading ? (
          <CardContent className="p-3">
            <AccountRowSkeleton />
            <AccountRowSkeleton />
            <AccountRowSkeleton />
          </CardContent>
        ) : error ? (
          <ErrorState message={error} onRetry={() => void fetch()} />
        ) : accounts.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No accounts connected"
            description="Connect an X account to start composing, scheduling and bulk-posting. You can connect as many as you like."
            action={{ label: 'Connect X account', onClick: () => void connect('/accounts') }}
          />
        ) : (
          <CardContent className="divide-y p-0">
            {accounts.map((account, index) => (
              <AccountRow
                key={account.id}
                account={account}
                index={index}
                onReconnect={() => void connect('/accounts')}
                onRemove={() => setConfirmRemove(account)}
              />
            ))}
          </CardContent>
        )}
      </Card>

      <ConfirmDialog
        open={confirmRemove !== null}
        onOpenChange={(open) => !open && setConfirmRemove(null)}
        title={`Disconnect @${confirmRemove?.username}?`}
        // Spell out both consequences: the scheduled posts, and the fact that
        // nothing already on X is affected.
        description="PostPilot will stop posting to this account and any scheduled posts targeting it will be cancelled. Posts already published on X are not affected. You can reconnect at any time."
        confirmLabel="Disconnect"
        destructive
        onConfirm={async () => {
          if (confirmRemove) await disconnect(confirmRemove.id);
        }}
      />
    </div>
  );
}

function AccountRow({
  account,
  index,
  onReconnect,
  onRemove,
}: {
  account: PublicAccount;
  index: number;
  onReconnect: () => void;
  onRemove: () => void;
}) {
  const isBroken = account.status !== 'active';

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.03, 0.15) }}
      className="flex flex-wrap items-center gap-4 p-4"
    >
      <div className="relative shrink-0">
        <Avatar className="size-10">
          <AvatarImage src={account.avatarUrl ?? undefined} alt="" />
          <AvatarFallback>{account.username.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <span
          className={cn(
            'border-card absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2',
            isBroken ? 'bg-status-failed' : 'bg-status-published',
          )}
          aria-hidden
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">
            {account.displayName ?? account.username}
          </span>
          <span className="text-muted-foreground truncate text-sm">@{account.username}</span>
        </div>

        {isBroken ? (
          <div className="text-status-failed mt-0.5 flex items-center gap-1.5 text-xs">
            <AlertCircle className="size-3 shrink-0" />
            <span className="truncate">
              {account.lastError ?? 'This account needs reconnecting.'}
            </span>
          </div>
        ) : (
          <div className="text-muted-foreground mt-0.5 text-xs">
            Connected {dayjs(account.createdAt).fromNow()}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1">
        {isBroken && (
          <Button size="sm" variant="outline" onClick={onReconnect}>
            <RefreshCw />
            Reconnect
          </Button>
        )}

        <Button
          size="icon-sm"
          variant="ghost"
          onClick={onRemove}
          aria-label={`Disconnect @${account.username}`}
          className="text-muted-foreground hover:text-destructive"
        >
          <Trash2 />
        </Button>
      </div>
    </motion.div>
  );
}
